import {
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  IconButton,
} from '@mui/material'
import BuildIcon from '@mui/icons-material/Build'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCodeValidationRule,
  deselectCodeValidationRule,
  selectCodeValidationRuleElement,
} from '../../actions/validation'
import {
  CodeDetail,
  CodeValidationRuleStatus,
  CodeValidationStatus,
  GroupedCodeDetailListProps,
} from '../../models/Validation'

const timelineColor = '#769DD1'

const statusColor = (status: CodeValidationStatus) => {
  return status === CodeValidationStatus.Failed ? '#FF2F5F' : '#388e3c'
}

const GroupedCodeDetailList = ({
  codes,
  elements,
  buildingData,
}: GroupedCodeDetailListProps) => {
  const dispatch = useDispatch()
  const selectedRule = useSelector(
    (state: any) => state.validation.selectedCodeValidationRule,
  )

  const getElementName = (elementId: string) => {
    const roofs = buildingData.building.roofs
    const stairs = buildingData.building.stairs
    const storeys = buildingData.building.storeys

    let elementType = 'Unknown'
    const isRoofElement = roofs.filter((roof) => roof.id === elementId)
    if (isRoofElement.length > 0) {
      elementType = 'Roof'
      return elementType
    }

    const isStairElement = stairs.filter((stair) => stair.id === elementId)
    if (isStairElement.length > 0) {
      elementType = 'Stair'
      return elementType
    }

    storeys.map((storey) => {
      const beams = storey.beams
      const isBeamElement = beams.filter((beam) => beam.id === elementId)
      if (isBeamElement.length > 0) {
        elementType = 'Beam'
      }
      const rooms = storey.rooms
      const isRoomElement = rooms.filter((room) => room.id === elementId)
      if (isRoomElement.length > 0) {
        elementType = 'Room'
      }

      const walls = storey.walls
      const isWallElement = walls.filter((wall) => wall.id === elementId)
      if (isWallElement.length > 0) {
        elementType = 'Wall'
      }
    })

    return elementType
  }

  const handleClick = useCallback(
    (item: CodeDetail, element: CodeValidationRuleStatus) => {
      if (selectedRule && selectedRule.code === item.codeReference) {
        dispatch(deselectCodeValidationRule())
      } else {
        dispatch(
          selectCodeValidationRule({
            item: {
              id: item.codeReference,
              codeId: item.codeId,
              codeReference: item.codeReference,
              discipline: item.discipline,
              description: item.description,
              atomics: item.atomics || [],
            },
          }),
        )
        dispatch(
          selectCodeValidationRuleElement({
            item: {
              ruleId: element.ruleId,
              elementId: element.elementId,
              discipline: element.discipline,
              status: element.status,
              elementType: element.elementType,
              fixingSuggestion: element.fixingSuggestion,
              code: {
                description: item.description || '',
                codeReference: item.codeReference || '',
                codeId: item.codeId || '',
                elements: [],
                atomics: [],
                status: item.status,
                conflict: 'Code Conflicts',
                discipline: item.discipline,
              },
            },
          }),
        )
      }
    },
    [dispatch, selectedRule],
  )

  return (
    <Box>
      <List dense sx={{ position: 'relative', pl: 0 }}>
        {elements.map((element, i) => {
          const elementName = getElementName(element.elementId)
          const elementStatus = element.status
          return (
            <ListItem
              key={element.elementId + i}
              sx={{
                borderBottom: '1px solid #eee',
                py: 0.5,
                pl: 0,
                '&:hover .fix-btn': { display: 'inline-flex' },
                position: 'relative',
                backgroundColor: 'transparent',
              }}
              disableGutters
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 18,
                  mr: 1,
                }}
              >
                {/* Dot */}
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: timelineColor,
                    zIndex: 1,
                  }}
                />
                {/* Connector line (above) */}
                {i !== 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 2,
                      height: 16,
                      bgcolor: timelineColor,
                      zIndex: 0,
                    }}
                  />
                )}
                {/* Connector line (below) */}
                {i !== elements.length - 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 2,
                      height: 22,
                      bgcolor: timelineColor,
                      zIndex: 0,
                    }}
                  />
                )}
              </Box>
              {/* Main content container with white background */}
              <Box
                display='flex'
                alignItems='center'
                width='100%'
                position='relative'
                bgcolor='#fff'
                borderRadius={2}
                px={2}
                py={1}
                onClick={() =>
                  handleClick(element.code!, {
                    ...element,
                    elementType: elementName,
                  })
                }
                sx={{ cursor: 'pointer' }}
              >
                {/* Code name */}
                <Box flex={1} minWidth={0}>
                  <Typography
                    fontWeight={500}
                    fontSize={13}
                    color='#04002E'
                    noWrap
                    sx={{
                      whiteSpace: 'nowrap',
                    }}
                    title={element.code?.description}
                  >
                    {elementName}
                  </Typography>
                </Box>
                {/* Code and status */}
                <Box
                  flex={1}
                  minWidth={0}
                  display='flex'
                  alignItems='center'
                  justifyContent='flex-end'
                  gap={2}
                >
                  {/* <Typography fontSize={11} color="#888" noWrap>
                    ({code.codeReference})
                  </Typography> */}
                  <Chip
                    label={elementStatus.toUpperCase()}
                    size='small'
                    sx={{
                      fontWeight: 600,
                      color: statusColor(elementStatus),
                      border: `1px solid ${statusColor(elementStatus)}`,
                      backgroundColor: 'transparent',
                      borderRadius: '4px',
                      height: 22,
                      minWidth: 54,
                      px: 0,
                      '& .MuiChip-label': { px: 0 },
                    }}
                  />
                </Box>
                <Box
                  minWidth={60}
                  display='flex'
                  alignItems='center'
                  justifyContent='flex-end'
                >
                  {elementStatus === CodeValidationStatus.Failed && (
                    <IconButton
                      className='fix-btn'
                      size='small'
                      sx={{
                        display: 'none',
                        p: 0.5,
                        backgroundColor: '#3F69FF',
                        borderRadius: '3px',
                        '&:hover': { backgroundColor: '#3356cc' },
                      }}
                      aria-label='fix'
                    >
                      <BuildIcon fontSize='small' sx={{ color: '#fff' }} />
                      <Typography
                        fontSize={11}
                        fontWeight={600}
                        ml={0.5}
                        color='#fff'
                      >
                        FIX
                      </Typography>
                    </IconButton>
                  )}
                </Box>
              </Box>
            </ListItem>
          )
        })}
      </List>
    </Box>
  )
}

export default GroupedCodeDetailList
